import React, { Fragment, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { clearErrorAction, applyCoupanAction } from '../../../redux/actions/coupanAction';
import Loader from '../../../layout/loader';
import swal from 'sweetalert';
import CheckoutStep from '../CheckoutStep';
import MetaTag from '../../../layout/MetaTag';
import '../confirmOrder.css';

function ConfirmOrdersForBuyDirect() {
    const dispatch = useDispatch();
    const history = useHistory();

    const { shippingInfo } = useSelector((state) => state.shippingInfo);
    // Getting data from localStorage for direct purchase
    const productBuy = JSON.parse(localStorage.getItem('productBuy') || '{}');

    const { error, loading, success, coupanCode, coupanValue } = useSelector((state) => state.coupanApply);

    // apply coupon
    const [applyCoupan, setApplyCoupan] = useState('');

    const quantity = Number(productBuy?.quantity || 1);
    const itemPrice = Number(productBuy?.price || 0);
    const selectBox = Number(productBuy?.selectBox || 0);

    // subtotal
    const subTotal = quantity * itemPrice;

    // Shipping charges:
    // If vendor set shipping price, show it; otherwise show 0 (FREE Delivery)
    const isFree = productBuy?.isdeliveryFree === true || productBuy?.isDeliveryFree === true;
    const rawShipping = (productBuy?.productShippingCharges !== undefined && productBuy?.productShippingCharges !== null)
        ? Number(productBuy?.productShippingCharges)
        : 0;
    const shippingCharges = isFree ? 0 : (rawShipping > 0 ? quantity * rawShipping : 0);

    // Box charges
    const boxCharges = quantity * selectBox;

    // GST Tax: Only applied if seller explicitly specified it
    let totalGstTax = 0;
    if (productBuy?.isGstApplicable === true && Number(productBuy?.gstPercent || 0) > 0) {
        totalGstTax = Math.round((subTotal * Number(productBuy.gstPercent)) / 100);
    }
    const roundOffTotalTax = Math.round(totalGstTax);

    // Total price
    const totalPrice = subTotal + roundOffTotalTax + shippingCharges + boxCharges;

    const address = `${shippingInfo.address}, ${shippingInfo.landMark ? `${shippingInfo.landMark}, ` : ''}${shippingInfo.city}, ${shippingInfo.state}, ${shippingInfo.district ? `${shippingInfo.district}, ` : ''}${shippingInfo.pincode}`;

    const proccedToPayments = () => {
        const data = {
            subTotal,
            itemPrice,
            shippingCharges,
            roundOffTotalTax,
            totalPrice,
            coupanCode,
            coupanValue,
            boxCharges
        };

        sessionStorage.setItem('orderInfo', JSON.stringify(data));
        history.push('/confirm/payments/directbuy');
    };

    const coupanApplySubmitHandler = (e) => {
        e.preventDefault();
        if (!applyCoupan.trim()) {
            return swal({
                title: "Coupon Required",
                text: "Please enter a valid coupon code.",
                icon: "info",
            });
        }
        dispatch(applyCoupanAction(applyCoupan.trim().toUpperCase(), totalPrice));
    };

    useEffect(() => {
        if (error) {
            swal({
                title: 'Coupon Error',
                text: error,
                icon: 'error'
            });
            dispatch(clearErrorAction());
        }
        if (success) {
            swal({
                title: 'Coupon Applied!',
                text: `Coupon ${coupanCode} applied successfully.`,
                icon: 'success'
            });
        }
    }, [dispatch, error, success, coupanCode]);

    return (
        <Fragment>
            {loading ? (
                <Loader />
            ) : (
                <Fragment>
                    <MetaTag title="Review & Confirm Your Order | IM Online Shopping" />
                    <CheckoutStep activeStep={1} />

                    <div className="confirm-order-page">
                        {/* LEFT COLUMN: Shipping info & Product details */}
                        <div className="confirm-left-column">
                            {/* Shipping Details */}
                            <div className="confirm-card confirm-shipping-card">
                                <div className="confirm-card-header">
                                    <div className="header-icon-wrap">
                                        <LocationOnOutlinedIcon />
                                    </div>
                                    <div>
                                        <h3>Shipping Address</h3>
                                        <p>Your order will be delivered to this verified location</p>
                                    </div>
                                    <Link to="/Checkout/directbuy" className="confirm-edit-link">
                                        Change
                                    </Link>
                                </div>

                                <div className="shipping-info-grid">
                                    <div className="shipping-info-row">
                                        <span className="label">Recipient:</span>
                                        <span className="value"><strong>{shippingInfo.name}</strong></span>
                                    </div>
                                    <div className="shipping-info-row">
                                        <span className="label">Contact:</span>
                                        <span className="value">{shippingInfo.phoneNo} {shippingInfo.email ? `• ${shippingInfo.email}` : ''}</span>
                                    </div>
                                    <div className="shipping-info-row">
                                        <span className="label">Delivery Address:</span>
                                        <span className="value address-text">{address}</span>
                                    </div>
                                </div>

                                <div className="shipping-cod-strip">
                                    <div className="cod-badge">
                                        <CheckCircleOutlineIcon style={{ fontSize: '18px', color: '#16a34a' }} />
                                        <span>Cash on Delivery Available</span>
                                    </div>
                                    <div className="shipping-speed-badge">
                                        <LocalShippingOutlinedIcon style={{ fontSize: '18px', color: '#1d4ed8' }} />
                                        <span>Standard Doorstep Delivery</span>
                                    </div>
                                </div>
                            </div>

                            {/* Product Item Card */}
                            <div className="confirm-card confirm-items-card">
                                <div className="confirm-card-header">
                                    <div className="header-icon-wrap">
                                        <ShoppingBagOutlinedIcon />
                                    </div>
                                    <div>
                                        <h3>Order Item (1)</h3>
                                        <p>Review the product details before proceeding to payment</p>
                                    </div>
                                </div>

                                <div className="confirm-cartItems-container">
                                    <div className="confirm-item-row">
                                        <div className="confirm-item-img-wrap">
                                            <img src={productBuy?.image} alt={productBuy?.name || 'Product'} />
                                        </div>

                                        <div className="confirm-item-info">
                                            <Link to={`/ProductDetail/${productBuy?.product}`} className="confirm-item-title">
                                                {productBuy?.name}
                                            </Link>
                                            <div className="confirm-item-specs">
                                                {productBuy?.size && <span className="spec-pill">Size: {productBuy.size}</span>}
                                                {productBuy?.colour && <span className="spec-pill">Color: {productBuy.colour}</span>}
                                                <span className="spec-pill cod-pill">✓ COD Eligible</span>
                                            </div>
                                            <div className="confirm-item-price-calc">
                                                <span className="calc-qty">{quantity} × ₹{itemPrice}</span>
                                                <span className="calc-total">
                                                    <CurrencyRupeeIcon style={{ fontSize: '14px' }} />
                                                    {quantity * itemPrice}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT COLUMN: Order Summary */}
                        <div className="confirm-right-column">
                            <div className="confirm-card order-summary-card">
                                <div className="confirm-card-header">
                                    <div className="header-icon-wrap">
                                        <PaymentsOutlinedIcon />
                                    </div>
                                    <div>
                                        <h3>Order Summary</h3>
                                        <p>Price breakdown for this order</p>
                                    </div>
                                </div>

                                <div className="summary-rows-group">
                                    <div className="summary-row">
                                        <span className="summary-label">Items Subtotal</span>
                                        <span className="summary-value">₹{subTotal}</span>
                                    </div>

                                    <div className="summary-row">
                                        <span className="summary-label">Shipping Charges</span>
                                        {shippingCharges === 0 ? (
                                            <span className="summary-value free-tag">FREE Delivery</span>
                                        ) : (
                                            <span className="summary-value">₹{shippingCharges}</span>
                                        )}
                                    </div>

                                    {boxCharges > 0 && (
                                        <div className="summary-row">
                                            <span className="summary-label">Packaging / Box</span>
                                            <span className="summary-value">₹{boxCharges}</span>
                                        </div>
                                    )}

                                    {roundOffTotalTax > 0 && (
                                        <div className="summary-row">
                                            <span className="summary-label">GST Tax</span>
                                            <span className="summary-value">₹{roundOffTotalTax}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="summary-total-row">
                                    <div>
                                        <strong>Total Amount</strong>
                                        <span className="tax-inclusive-note">
                                            {roundOffTotalTax > 0 ? 'Includes all applicable taxes' : 'All taxes included'}
                                        </span>
                                    </div>
                                    <div className="total-amount-display">
                                        <CurrencyRupeeIcon style={{ fontSize: '20px' }} />
                                        <span>{totalPrice}</span>
                                    </div>
                                </div>

                                {/* Coupon Section */}
                                <div className="coupon-box">
                                    <div className="coupon-title">Have a coupon or promo code?</div>
                                    <form onSubmit={coupanApplySubmitHandler} className="coupon-input-group">
                                        <input
                                            type="text"
                                            className="coupon-input"
                                            placeholder="Enter coupon code"
                                            value={applyCoupan}
                                            onChange={(e) => setApplyCoupan(e.target.value)}
                                        />
                                        <button type="submit" className="coupon-apply-btn">
                                            Apply
                                        </button>
                                    </form>

                                    {success && coupanCode && (
                                        <div className="coupon-applied-badge">
                                            <div>
                                                <strong>{coupanCode} Applied!</strong>
                                                <span>Discount value: ₹{coupanValue}</span>
                                            </div>
                                            <span className="coupon-check">✓</span>
                                        </div>
                                    )}
                                </div>

                                <button className="proceed-to-payment-btn" onClick={proccedToPayments}>
                                    Proceed to Payment &rarr;
                                </button>

                                <div className="confirm-trust-strip">
                                    <div className="trust-item">
                                        <VerifiedUserOutlinedIcon style={{ fontSize: '16px', color: '#16a34a' }} />
                                        <span>100% Safe Checkout</span>
                                    </div>
                                    <div className="trust-item">
                                        <CheckCircleOutlineIcon style={{ fontSize: '16px', color: '#16a34a' }} />
                                        <span>Cash on Delivery Option</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Fragment>
            )}
        </Fragment>
    );
}

export default ConfirmOrdersForBuyDirect;
